import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useQuery } from "@tanstack/react-query";
import { Aside } from "styles/jss/News.styles";
import { ArrowLeft, ArrowRight } from "../../IconsSvg";
import { getArchiveList } from "services/archive.api";
import { Skeleton } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { useState } from "react";

export default function AsideArchive({ title, url }) {
  const { locale, query } = useRouter();
  const { t } = useTranslation(["article"]);

  const {
    data: archiveList,
    isLoading,
    isError,
  } = useQuery(["archive-list", locale], () => getArchiveList(locale, url), {
    staleTime: 0,
    cacheTime: Infinity,
    select: data => {
      const handle_orderMonth = date => {
        const conditionValue = date.trim().toLowerCase();
        if (locale === "fa") {
          switch (conditionValue) {
            case "فروردین":
              return 1;
            case "اردیبهشت":
              return 2;
            case "خرداد":
              return 3;
            case "تیر":
              return 4;
            case "مرداد":
              return 5;
            case "شهریور":
              return 6;
            case "مهر":
              return 7;
            case "آبان":
              return 8;
            case "آذر":
              return 9;
            case "دی":
              return 10;
            case "بهمن":
              return 11;
            case "اسفند":
              return 12;
          }
        } else {
          switch (conditionValue) {
            case "april":
              return 1;
            case "may":
              return 2;
            case "june":
              return 3;
            case "july":
              return 4;
            case "august":
              return 5;
            case "september":
              return 6;
            case "october":
              return 7;
            case "november":
              return 8;
            case "december":
              return 9;
            case "january":
              return 10;
            case "february":
              return 11;
            case "march":
              return 12;
          }
        }
      };
      const res = data.data.reduce((newArr, arr) => {
        const key = arr.attributes.slug.toString().split("-")[1];
        const date = arr.attributes.date.toString().trim().split(" ")[0];
        const newArrayKeys = newArr.map(item => Object.keys(item)).flat();

        if (!newArrayKeys.includes(key)) {
          newArr.push({
            [key]: new Array({
              month_id: handle_orderMonth(date),
              date,
              slug: arr.attributes.slug.toString(),
            }),
          });
        } else {
          const index = newArrayKeys.indexOf(key);
          newArr[index][key].push({
            month_id: handle_orderMonth(date),
            date,
            slug: arr.attributes.slug.toString(),
          });
        }

        return [...newArr];
      }, []);
      const sortedRes = res
        .sort((a, b) => {
          const indexA = Object.keys(a)[0];
          const indexB = Object.keys(b)[0];
          return indexA - indexB;
        })
        .reverse();
      return res.map(item => {
        const key = Object.keys(item)[0]; // get the key (year) of the item
        const sortedValues = item[key].sort((a, b) => a.month_id - b.month_id); // sort the values by month_id
        return { [key]: sortedValues }; // return the sorted item
      });
    },
  });

  const [expanded, setExpanded] = useState(query?.archive?.split("-")[1]);
  const handleChange = panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (isLoading)
    return (
      <div>
        <Skeleton animation="wave" />
        <Skeleton animation="wave" />
        <Skeleton animation="wave" />
        <Skeleton animation="wave" />
      </div>
    );

  if (isError) return <div>...error</div>;

  return (
    <Aside>
      <div className="archive">
        <div className="header">
          <h3 className="title">{t(title)}</h3>
          <Link href={`${url}`} locale={locale} shallow>
            <a>
              <span className="seeAll">{t("seeAll")}</span>
            </a>
          </Link>
        </div>
        <ul>
          {archiveList.map(item => {
            const title = Object.keys(item)[0];

            return (
              <Accordion
                key={title}
                expanded={expanded === title}
                onChange={handleChange(title)}
              >
                <AccordionSummary
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                  expandIcon={
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M17 10L12 15L7 10"
                        stroke="#737373"
                        strokeWidth="1"
                        strokeLinecap="round"
                      />
                    </svg>
                  }
                >
                  {title}
                </AccordionSummary>
                <AccordionDetails>
                  {item[title].map(({ date, slug }) => {
                    return (
                      <Link
                        key={slug}
                        href={`${url}?archive=${slug}`}
                        locale={locale}
                        shallow
                      >
                        <a>
                          <span>{date}</span>
                          <i>
                            {locale === "fa" ? <ArrowLeft /> : <ArrowRight />}
                          </i>
                        </a>
                      </Link>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </ul>
      </div>
    </Aside>
  );
}
